import type { KeyboardEvent } from "react";
import type { ReportConfigurationLookupOption } from "../types";
import { ChipList } from "./ChipList";
import { ValuePicker } from "./ValuePicker";

/* Shared "pick from the list OR type your own" value entry.
   Extracted verbatim from the Categorization tab's multi-value condition branch so the
   Metrics filter sections ("Inclusion Criteria" / "Exclusion Criteria") get the
   exact same control instead of a second, drifting copy. Dedupe stays case-SENSITIVE and
   blank-guarded, which is what addChipValue has always done for Categorization - changing
   that here would silently change how already-saved rule conditions behave. */

export const addChipValue = (values: string[], value: string) =>
    value && !values.includes(value) ? [...values, value] : values;

export const removeChipValue = (values: string[], value: string) =>
    values.filter((item) => item !== value);

interface ValueMultiSelectProps {
    options: ReportConfigurationLookupOption[];
    values: string[];
    onChange: (values: string[]) => void;
    listPlaceholder?: string;
    customPlaceholder?: string;
    emptyText?: string;
    getLabel?: (value: string) => string;
    disabled?: boolean;
    searchable?: boolean;
    searchThreshold?: number;
}

export function ValueMultiSelect({
    options,
    values,
    onChange,
    listPlaceholder = "Add From List...",
    customPlaceholder = "Type a Custom Value, Then Press Enter",
    emptyText = "Add one or more values. Matching uses ANY selected value.",
    getLabel,
    disabled,
    searchable,
    searchThreshold,
}: ValueMultiSelectProps) {
    // Typed values are trimmed before they become chips; values picked from the lookup list
    // are passed through untouched so they still resolve to their lookup id at save time.
    const addCustomValue = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        const typed = event.currentTarget.value.trim();
        event.currentTarget.value = "";
        if (!typed) return;
        onChange(addChipValue(values, typed));
    };

    return (
        <>
            <div className="rc-condition-value-entry">
                <ValuePicker
                    options={options}
                    placeholder={listPlaceholder}
                    selectedValues={values}
                    selectedCount={values.length}
                    totalCount={options.length}
                    disabled={disabled}
                    searchable={searchable}
                    searchThreshold={searchThreshold}
                    onPick={(value) => onChange(addChipValue(values, value))}
                    onToggle={(value) => onChange(values.includes(value) ? removeChipValue(values, value) : addChipValue(values, value))}
                    onPickMany={(picked) => onChange(picked.reduce((next, value) => addChipValue(next, value), values))}
                    onClear={() => onChange([])}
                />
                <input
                    className="rc-custom-value"
                    disabled={disabled}
                    placeholder={customPlaceholder}
                    onKeyDown={addCustomValue}
                />
            </div>
            <ChipList
                values={values}
                onRemove={(value) => onChange(removeChipValue(values, value))}
                emptyText={emptyText}
                getLabel={getLabel}
            />
        </>
    );
}
